import { Head, Link } from "@inertiajs/react";
import { ArrowRight } from "lucide-react";

import { ActivationGuide } from "@/components/activation-guide";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface NextjsOgFaq {
  question: string;
  answer: string;
}

interface NextjsOgImageProps {
  page_title?: string;
  faqs?: NextjsOgFaq[];
}

interface ComparisonRow {
  dimension: string;
  file: string;
  imageResponse: string;
  hosted: string;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  {
    dimension: "What you add",
    file: "opengraph-image or twitter-image in the route segment",
    imageResponse: "JSX returned from ImageResponse (next/og or @vercel/og)",
    hosted: "A CDN URL from generateMetadata / openGraph.images",
  },
  {
    dimension: "Who writes the meta tags",
    file: "Next.js writes og:image, type, width, height, and alt",
    imageResponse: "The file convention writes them. A custom route does not.",
    hosted: "You set openGraph.images and twitter.images",
  },
  {
    dimension: "Dynamic per URL",
    file: "A static image is one card per segment. A code file can read params.",
    imageResponse: "Yes, from params, fetch, or query string",
    hosted: "Yes. Pass template, title, description, and path per page.",
  },
  {
    dimension: "Where it runs",
    file: "Your Next.js build or server",
    imageResponse: "Your Next.js route, or @vercel/og on another framework on Vercel",
    hosted: "OG Pilot CDN. The signing app can be anywhere.",
  },
  {
    dimension: "How you design the card",
    file: "A finished PNG/JPG, or JSX inside opengraph-image.tsx",
    imageResponse: "JSX plus a flexbox CSS subset",
    hosted: "Named templates such as page, blog_post, and product",
  },
  {
    dimension: "CSS and size limits",
    file: "Static opengraph-image max 8MB. twitter-image max 5MB. Code files share the ImageResponse limits.",
    imageResponse: "No display:grid. Bundle max 500KB including JSX, CSS, fonts, and images.",
    hosted: "Template fields. You do not author per-request CSS.",
  },
  {
    dimension: "Fonts",
    file: "Baked into a static image, or ttf / otf / woff inside ImageResponse",
    imageResponse: "ttf, otf, and woff. ttf or otf is preferred.",
    hosted: "Handled by the template",
  },
  {
    dimension: "Caching",
    file: "Generated images are statically optimized unless they use request-time APIs or uncached data.",
    imageResponse: "Production @vercel/og sets cache-control: public, immutable, no-transform, max-age=31536000.",
    hosted: "CDN URL. Optional iat refreshes the cache for 24 hours from that timestamp.",
  },
  {
    dimension: "Other frameworks and agents",
    file: "Next.js App Router only",
    imageResponse: "Vercel documents @vercel/og for any framework deployed on Vercel. MCP is yours to build.",
    hosted: "JS, Ruby, Python, PHP, WordPress, and MCP at https://ogpilot.com/mcp",
  },
  {
    dimension: "Latency",
    file: "NEED VERIFY",
    imageResponse: "NEED VERIFY",
    hosted: "NEED VERIFY",
  },
  {
    dimension: "Cost",
    file: "NEED CITE from your host",
    imageResponse: "NEED CITE from your function and bandwidth plan",
    hosted: "NEED CITE. Current plans are in billing after signup.",
  },
];

const FILE_CONVENTION_SAMPLE = `app/blog/[slug]/opengraph-image.png
app/blog/[slug]/opengraph-image.alt.txt   ← plain text alt

Next.js adds, for that segment:
<meta property="og:image" content="<generated>" />
<meta property="og:image:type" content="<generated>" />
<meta property="og:image:width" content="<generated>" />
<meta property="og:image:height" content="<generated>" />
<meta property="og:image:alt" content="text from the .alt.txt file" />

twitter-image.(jpg|jpeg|png|gif) does the same for twitter:image.
Supported static types: .jpg, .jpeg, .png, .gif.`;

const OPENGRAPH_CODE_SAMPLE = `import { ImageResponse } from "next/og"

export const alt = "Blog post"
export const size = { width: 1200, height: 630 }
export const contentType = "image/png"

// Next.js 16: params is a Promise. On 15 and earlier it is a plain object.
export default async function Image({
  params,
}: {
  params: Promise<{ slug: string }>
}) {
  const { slug } = await params
  const post = await getPost(slug)

  return new ImageResponse(
    (
      <div
        style={{
          fontSize: 64,
          background: "white",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 48,
        }}
      >
        {post.title}
      </div>
    ),
    { ...size },
  )
}`;

const ROUTE_HANDLER_SAMPLE = `// app/api/og/route.tsx
import { ImageResponse } from "next/og"
// App Router includes @vercel/og. No separate install.

export function GET(request: Request) {
  const { searchParams } = new URL(request.url)
  const title = searchParams.get("title") ?? "Hello"

  return new ImageResponse(
    (
      <div
        style={{
          fontSize: 56,
          background: "#fff7ed",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {title}
      </div>
    ),
    { width: 1200, height: 630 },
  )
}

// app/blog/[slug]/page.tsx — point metadata at the absolute URL
export async function generateMetadata() {
  const ogImage = "https://example.com/api/og?title=Hello"
  return {
    openGraph: { images: [ogImage] },
    twitter: { card: "summary_large_image", images: [ogImage] },
  }
}`;

const OG_PILOT_SAMPLE = `import { buildPathFromNextProps, configure, createImage } from "og-pilot-js"

configure((config) => {
  config.apiKey = process.env.OG_PILOT_API_KEY
  config.domain = process.env.OG_PILOT_DOMAIN
})

export async function generateMetadata(props) {
  const path = await buildPathFromNextProps("/blog/[slug]", props)
  const ogImage = await createImage(
    {
      template: "blog_post",
      title: "Post title",
      description: "Post excerpt",
    },
    { path, iat: Date.now() },
  )

  return {
    openGraph: { images: [ogImage] },
    twitter: { card: "summary_large_image", images: [ogImage] },
  }
}`;

const RAW_HTTP_SAMPLE = `POST https://ogpilot.com/api/v1/images
Content-Type: application/json

{"token":"YOUR_JWT_TOKEN"}`;

const MCP_POINTER_SAMPLE = `Remote MCP URL: https://ogpilot.com/mcp
Docs: https://ogpilot.com/docs/mcp
OAuth: follow the custom connector guide on /docs/mcp (OAuth 2.1 + PKCE)`;

const sectionClass =
  "mt-20 rounded-3xl border border-orange-200/80 bg-white/90 p-8 shadow-xl dark:border-orange-500/25 dark:bg-slate-900/80 dark:shadow-none";

const linkClass = "font-medium text-orange-700 hover:underline dark:text-orange-300";

function CodeSample({ label, code }: { label: string; code: string }) {
  return (
    <div className="mt-6">
      <p className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</p>
      <pre className="overflow-x-auto rounded-2xl border border-orange-200 bg-orange-50 p-5 text-sm leading-relaxed text-slate-900 dark:border-orange-500/30 dark:bg-slate-950 dark:text-orange-50">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function NextjsOgImage({
  page_title: pageTitle = "Next.js OG Image: File vs ImageResponse",
  faqs = [],
}: NextjsOgImageProps) {
  return (
    <>
      <Head title={pageTitle} />

      <div className="min-h-screen bg-[radial-gradient(circle_at_0%_10%,#fed7aa_0,transparent_45%),radial-gradient(circle_at_100%_0%,#fecaca_0,transparent_38%),linear-gradient(180deg,#fff7ed_0%,#ffffff_32%,#fffbeb_100%)] pb-20 dark:bg-[radial-gradient(circle_at_0%_10%,#431407_0,transparent_45%),radial-gradient(circle_at_100%_0%,#7f1d1d_0,transparent_38%),linear-gradient(180deg,#0f172a_0%,#111827_32%,#0b1120_100%)]">
        <div className="container mx-auto max-w-6xl px-4 py-12">
          <PublicHeader />

          <section className="relative mt-8 overflow-hidden rounded-[2.25rem] border border-orange-200/80 bg-white/85 p-8 shadow-[0_35px_70px_-40px_rgba(194,65,12,0.55)] backdrop-blur sm:p-10 dark:border-orange-500/30 dark:bg-slate-900/80 dark:shadow-[0_35px_70px_-40px_rgba(249,115,22,0.35)]">
            <div className="pointer-events-none absolute -top-16 -right-10 h-48 w-48 rounded-full bg-red-300/35 blur-3xl dark:bg-red-500/20" />
            <div className="pointer-events-none absolute -bottom-20 -left-10 h-52 w-52 rounded-full bg-orange-300/40 blur-3xl dark:bg-orange-500/20" />

            <div className="relative">
              <Badge className="mb-5 border border-orange-200 bg-orange-100 px-4 py-1.5 tracking-[0.22em] text-orange-700 uppercase dark:border-orange-500/40 dark:bg-orange-500/20 dark:text-orange-100">
                Next.js OG image
              </Badge>

              <h1 className="max-w-4xl text-4xl leading-tight font-bold text-balance text-slate-900 sm:text-5xl lg:text-6xl dark:text-slate-100">
                Next.js OG image: metadata file vs ImageResponse vs hosted API
              </h1>

              <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-700 sm:text-lg dark:text-slate-300">
                A <strong>Next.js OG image</strong> can be a file in the route, JSX from{" "}
                <strong>ImageResponse / @vercel/og</strong>, or a CDN URL from a hosted API. This page is the decision
                guide: when the metadata file convention is enough, when <code>vercel/og</code> is the right renderer,
                and when OG Pilot wins because the same card has to leave Next.js.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Button asChild size="lg" className="bg-orange-500 text-white hover:bg-orange-600">
                  <Link href="/sign_up">
                    Get API key
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <div className="grid grid-cols-2 gap-3 sm:flex">
                  <Button asChild size="lg" variant="outline">
                    <Link href="/docs/mcp">Use MCP</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link href="/new">Generate</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link href="/docs">Docs</Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              When to use each Next.js OG image path
            </h2>
            <div className="mt-6 grid gap-4 lg:grid-cols-3">
              <article
                id="metadata-file"
                className="rounded-2xl border border-orange-100 bg-orange-50/70 p-5 dark:border-orange-500/20 dark:bg-slate-950/60"
              >
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Metadata file (opengraph-image)
                </h3>
                <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-slate-700 dark:text-slate-300">
                  <li>One designed PNG or JPG covers the whole segment (about, pricing, a campaign).</li>
                  <li>You want Next.js to emit og:image, type, width, height, and alt without a metadata export.</li>
                  <li>
                    The card changes with the route, and colocating <code>opengraph-image.tsx</code> next to the page is
                    clearer than a shared API route.
                  </li>
                </ul>
              </article>
              <article
                id="image-response"
                className="rounded-2xl border border-orange-100 bg-orange-50/70 p-5 dark:border-orange-500/20 dark:bg-slate-950/60"
              >
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">ImageResponse / @vercel/og</h3>
                <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-slate-700 dark:text-slate-300">
                  <li>The layout is JSX you want in the Next.js repo.</li>
                  <li>Many pages can share one Route Handler, with the title passed as a query param.</li>
                  <li>You accept the Satori CSS subset, the 500KB bundle cap, and Vercel function hosting.</li>
                </ul>
              </article>
              <article
                id="hosted-api"
                className="rounded-2xl border border-orange-100 bg-orange-50/70 p-5 dark:border-orange-500/20 dark:bg-slate-950/60"
              >
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Hosted OG image API</h3>
                <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-slate-700 dark:text-slate-300">
                  <li>Rails, Astro, WordPress, or a non-Vercel host need the same templates.</li>
                  <li>Editors should pick a template, not maintain Edge CSS.</li>
                  <li>
                    An agent should generate the card through{" "}
                    <Link href="/docs/mcp" className={linkClass}>
                      MCP
                    </Link>
                    .
                  </li>
                </ul>
              </article>
            </div>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Feature comparison</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Limits below are from the Next.js and Vercel docs linked in each section. OG Pilot cache behavior is from
              the{" "}
              <Link href="/docs" className={linkClass}>
                API docs
              </Link>
              . Latency and public list prices stay marked until there is a source.
            </p>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full min-w-[52rem] border-collapse text-left text-sm text-slate-700 dark:text-slate-300">
                <thead>
                  <tr className="border-b border-orange-200 dark:border-orange-500/30">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Dimension</th>
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Metadata file</th>
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">
                      ImageResponse / @vercel/og
                    </th>
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Hosted OG Pilot API</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row) => (
                    <tr key={row.dimension} className="border-b border-orange-100 align-top dark:border-orange-500/20">
                      <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">{row.dimension}</th>
                      <td className="px-3 py-3">{row.file}</td>
                      <td className="px-3 py-3">{row.imageResponse}</td>
                      <td className="px-3 py-3">{row.hosted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Recommended OG size on the Vercel side is 1200×630 (
              <a
                href="https://vercel.com/docs/og-image-generation"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Vercel OG Image Generation
              </a>
              ). <code>ImageResponse</code> defaults to that size too.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              Metadata file: <code>opengraph-image</code> and <code>twitter-image</code>
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Next.js App Router treats <code>opengraph-image</code> and <code>twitter-image</code> as file conventions.
              Drop a static <code>.jpg</code>, <code>.jpeg</code>, <code>.png</code>, or <code>.gif</code> in the
              segment and Next.js adds the meta tags. Put the alt text in <code>opengraph-image.alt.txt</code> or{" "}
              <code>twitter-image.alt.txt</code>. The build fails if a static <code>opengraph-image</code> is over 8MB
              or a static <code>twitter-image</code> is over 5MB.
            </p>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              A code file (<code>.js</code>, <code>.ts</code>, <code>.tsx</code>) default-exports a function that
              returns a <code>Response</code>. <code>ImageResponse</code> satisfies that. Optional exports are{" "}
              <code>alt</code>, <code>size</code>, and <code>contentType</code>. Generated images are statically
              optimized unless they touch request-time APIs or uncached data. <code>generateImageMetadata</code> returns
              more than one image from the same file. Since Next.js 16, <code>params</code> is a Promise. The convention
              shipped in v13.3.0.
            </p>
            <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Source:{" "}
              <a
                href="https://nextjs.org/docs/app/api-reference/file-conventions/metadata/opengraph-image"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Next.js opengraph-image
              </a>
              .
            </p>
            <CodeSample label="Static file convention" code={FILE_CONVENTION_SAMPLE} />
            <CodeSample label="Dynamic opengraph-image.tsx (ImageResponse)" code={OPENGRAPH_CODE_SAMPLE} />
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              You do not also set <code>openGraph.images</code> for a file in the same segment. Next.js already emits
              the tags. Reach for <code>generateMetadata</code> when the image URL is external, including a hosted API.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              ImageResponse and <code>@vercel/og</code>
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              <code>ImageResponse</code> from <code>next/og</code> uses <code>@vercel/og</code>, Satori, and Resvg to
              convert JSX into PNG. The default size is 1200×630. Only flexbox and a CSS subset work.{" "}
              <code>display: grid</code> does not. The bundle, including JSX, CSS, fonts, and images, must stay under
              500KB. Fonts are <code>ttf</code>, <code>otf</code>, and <code>woff</code>, and <code>ttf</code> or{" "}
              <code>otf</code> parse faster. The supported CSS list lives in{" "}
              <a href="https://github.com/vercel/satori" className={linkClass} target="_blank" rel="noreferrer">
                Satori
              </a>
              .
            </p>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              App Router projects already include <code>@vercel/og</code>. Other frameworks install the package. Vercel
              documents it as compatible with any framework deployed on Vercel, and as supported on the Node.js runtime.
              Pages Router plus the Node.js runtime does not support <code>return new Response(…)</code> with{" "}
              <code>vercel/og</code>. App Router with Node.js or Edge, and Pages Router with Edge, do. In production,{" "}
              <code>@vercel/og</code> sets <code>cache-control: public, immutable, no-transform, max-age=31536000</code>
              . Change the URL when the artwork must change. If crawlers fetch <code>/api/og</code>, allow that path in{" "}
              <code>robots.txt</code>.
            </p>
            <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Sources:{" "}
              <a
                href="https://nextjs.org/docs/app/api-reference/functions/image-response"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Next.js ImageResponse
              </a>
              ,{" "}
              <a
                href="https://vercel.com/docs/og-image-generation"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Vercel OG Image Generation
              </a>
              ,{" "}
              <a
                href="https://vercel.com/docs/og-image-generation/og-image-api"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                @vercel/og API
              </a>
              .
            </p>
            <CodeSample label="Shared Route Handler plus generateMetadata" code={ROUTE_HANDLER_SAMPLE} />
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">When a hosted OG image API wins</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              OG Pilot returns a CDN URL you place in <code>og:image</code>. Signing stays on your server: the
              JavaScript SDK reads <code>OG_PILOT_API_KEY</code> and <code>OG_PILOT_DOMAIN</code>, signs a JWT, and{" "}
              <code>createImage</code> returns the URL. <code>buildPathFromNextProps</code> fills the path claim from
              App Router props. Pass <code>iat</code> when you want the documented 24-hour cache window. Omit it and the
              cache does not expire on its own. Social crawlers then fetch the image from the CDN, which is the caching
              model for multi-framework sites that are not on Vercel.
            </p>
            <ul className="mt-4 list-disc space-y-2 pl-5 text-slate-700 dark:text-slate-300">
              <li>Same templates on Next.js, Rails, Astro, Python, PHP, and WordPress.</li>
              <li>
                Design stays in templates (<code>blog_post</code>, <code>product</code>, <code>page</code>, and others)
                instead of a 500KB Satori bundle.
              </li>
              <li>
                Agents call the remote MCP endpoint. Install steps:{" "}
                <Link href="/docs/mcp" className={linkClass}>
                  /docs/mcp
                </Link>
                .
              </li>
            </ul>
            <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Source:{" "}
              <Link href="/docs" className={linkClass}>
                https://ogpilot.com/docs
              </Link>
              . The same API is what the{" "}
              <Link href="/rails-og-image-generator" className={linkClass}>
                Rails
              </Link>{" "}
              and{" "}
              <Link href="/astro-og-image-generator" className={linkClass}>
                Astro
              </Link>{" "}
              guides use. WordPress sites can use the{" "}
              <Link href="/wordpress" className={linkClass}>
                plugin
              </Link>{" "}
              instead of <code>generateMetadata</code>.
            </p>
            <CodeSample label="Next.js + og-pilot-js (server only)" code={OG_PILOT_SAMPLE} />
            <CodeSample label="Raw HTTP shape" code={RAW_HTTP_SAMPLE} />
            <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Keep the API key out of client components. Prefer the SDK over hand-rolled JWT signing.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">MCP install pointer</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              The remote endpoint is <code>https://ogpilot.com/mcp</code>. Documented tools include{" "}
              <code>generate_og_image</code>, <code>debug_open_graph</code>, <code>compress_image</code>, and{" "}
              <code>extract_color_palette</code>. Connector JSON differs by client, so follow{" "}
              <Link href="/docs/mcp" className={linkClass}>
                /docs/mcp
              </Link>{" "}
              instead of a guessed config. After you publish a card,{" "}
              <Link href="/open-graph-debugger" className={linkClass}>
                check it in the Open Graph debugger
              </Link>
              .
            </p>
            <CodeSample label="MCP endpoint pointer" code={MCP_POINTER_SAMPLE} />
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              Latency and cost — labeled, not invented
            </h2>
            <ul className="mt-4 list-disc space-y-3 pl-5 text-base leading-7 text-slate-700 dark:text-slate-300">
              <li>
                <strong>Latency: NEED VERIFY.</strong> Time a metadata-file hit, a Vercel function region, and an OG
                Pilot CDN URL on your own pages. A warm social cache is a different number from the first fetch.
              </li>
              <li>
                <strong>Cost: NEED CITE.</strong> Function and bandwidth prices belong to your host&apos;s pricing page.
                OG Pilot plans are shown in billing after{" "}
                <Link href="/sign_up" className={linkClass}>
                  signup
                </Link>
                . This page does not publish a cost per 1,000 images.
              </li>
              <li>
                <strong>Engineering cost</strong> is the part the matrix cannot price: Satori CSS, font subsetting, and
                the 500KB cap versus a template you do not host.
              </li>
            </ul>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Decision checklist</h2>
            <ol className="mt-4 list-decimal space-y-2 pl-5 text-base leading-7 text-slate-700 dark:text-slate-300">
              <li>One unchanging card for the segment? Add a static opengraph-image file and an alt text file.</li>
              <li>
                Card follows the route, and Next.js should write the tags? Use <code>opengraph-image.tsx</code>.
              </li>
              <li>Many routes, one JSX layout, still on Next.js? Use a Route Handler and generateMetadata.</li>
              <li>More than one framework, a non-Vercel host, or an agent? Use the hosted API and MCP.</li>
              <li>Put an absolute og:image URL in the HTML, then re-scrape after the artwork changes.</li>
            </ol>
          </section>

          <ActivationGuide
            title="Ship the Next.js OG image path you actually need"
            links={[
              {
                href: "/sign_up",
                label: "Get an API key",
                detail: "Create an account, verify a domain, and issue a signing key.",
              },
              {
                href: "/docs/mcp",
                label: "Use MCP",
                detail: "Install the remote connector so an agent can generate the card.",
              },
              {
                href: "/new",
                label: "Generate one image",
                detail: "Preview a branded card before you wire production auth.",
              },
              {
                href: "/docs",
                label: "API docs",
                detail: "JWT claims, templates, iat cache rules, and the JavaScript SDK.",
              },
              {
                href: "/open-graph-debugger",
                label: "Open Graph debugger",
                detail: "Confirm the card before Facebook, X, LinkedIn, or Slack cache it.",
              },
              {
                href: "/rails-og-image-generator",
                label: "Rails guide",
                detail: "The same hosted API from a Rails layout, outside ImageResponse.",
              },
            ]}
          >
            <p>
              Pick the metadata file or <code>@vercel/og</code> when the card lives entirely in one Next.js app. Get an
              API key when the preview also has to exist for Rails, Astro, WordPress, or an MCP client.{" "}
              <Link href="/astro-og-image-generator" className={linkClass}>
                Astro
              </Link>{" "}
              uses that same CDN URL. Generate a sample on{" "}
              <Link href="/new" className={linkClass}>
                /new
              </Link>{" "}
              if you want to see the template before you install the SDK.
            </p>
          </ActivationGuide>

          {faqs.length > 0 ? (
            <section className={sectionClass}>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Next.js OG image questions</h2>
              <div className="mt-6 space-y-6">
                {faqs.map((faq) => (
                  <article key={faq.question}>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{faq.question}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{faq.answer}</p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <section className="mt-20 rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-orange-900 p-10 text-white shadow-xl">
            <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
              <div>
                <h2 className="text-3xl font-bold sm:text-4xl">Get an API key or connect MCP</h2>
                <p className="mt-4 max-w-xl text-sm leading-6 text-slate-200">
                  Keep <code>opengraph-image</code> or <code>ImageResponse</code> for a single Next.js app. Use OG Pilot
                  when the card has to travel with an API key or an MCP install.
                </p>
              </div>
              <div className="rounded-2xl border border-white/20 bg-white/10 p-6">
                <div className="flex flex-col gap-3">
                  <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
                    <Link href="/sign_up">
                      Get API key
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Button
                      asChild
                      variant="outline"
                      className="border-white/40 bg-transparent text-white hover:bg-white/10"
                    >
                      <Link href="/docs/mcp">Use MCP</Link>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="border-white/40 bg-transparent text-white hover:bg-white/10"
                    >
                      <Link href="/docs">Docs</Link>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="border-white/40 bg-transparent text-white hover:bg-white/10"
                    >
                      <Link href="/new">Generate</Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <SiteFooter className="mt-16" showSeparator={false} />
        </div>
      </div>
    </>
  );
}
